// Logo Ambar Parametreleri sayfasi. Swagger'da yalnizca PUT var => gonderim odakli:
// malzeme kodu + ambar satirlari gir -> Logo'ya PUT. Gonderilenler ust listede birikir.
import { useCallback, useRef, useState } from 'react';
import DataGrid, { Column, Editing, Scrolling } from 'devextreme-react/data-grid';
import Splitter, { Item as SplitterItem } from 'devextreme-react/splitter';
import TextBox from 'devextreme-react/text-box';
import Button from 'devextreme-react/button';
import notify from 'devextreme/ui/notify';
import { CustomDataGrid } from '../components/CustomDataGrid';
import { ItemWarehouseParam, WarehouseParamLine } from '../types';
import { saveWarehouseParam } from '../api/warehouse-params';
import { lt } from '../i18n';

let seq = 0;
const store: Array<ItemWarehouseParam & { _key: number }> = [];

const listColumns = [
  { dataField: 'itemCode', caption: 'Malzeme Kodu', width: 200 },
  { dataField: 'lines', caption: 'Ambar Sayisi', width: 130, alignment: 'center' as const,
    cellRender: (d: any) => (d.data.lines?.length ?? 0) },
];

export default function LogoWarehouseParamsPage() {
  const [rows, setRows] = useState<Array<ItemWarehouseParam & { _key: number }>>([...store]);
  const [itemCode, setItemCode] = useState('');
  const [lines, setLines] = useState<WarehouseParamLine[]>([]);
  const [sending, setSending] = useState(false);
  const linesRef = useRef<WarehouseParamLine[]>(lines);
  linesRef.current = lines;

  const reloadList = useCallback(() => setRows([...store]), []);

  const send = useCallback(async () => {
    if (!itemCode.trim()) { notify('Malzeme Kodu ?', 'warning', 2500); return; }
    const param: ItemWarehouseParam = { itemCode: itemCode.trim(), lines: linesRef.current };
    setSending(true);
    const res = await saveWarehouseParam(param);
    setSending(false);
    if (res.ok) {
      store.push({ ...param, _key: ++seq });
      reloadList();
      notify(lt('savedOk'), 'success', 2500);
      setItemCode(''); setLines([]);
    } else {
      notify(`${lt('saveError')}: ${res.message ?? ''}`, 'error', 4000);
    }
  }, [itemCode, reloadList]);

  const onSelect = useCallback((p: ItemWarehouseParam) => {
    setItemCode(p.itemCode);
    setLines(p.lines ? p.lines.map((l) => ({ ...l })) : []);
  }, []);

  return (
    <div style={{ height: 'calc(100vh - 120px)' }}>
      <Splitter orientation="vertical" style={{ height: '100%' }}>
        <SplitterItem size="45%" minSize="120px" collapsible>
          <CustomDataGrid
            data={rows}
            columns={listColumns}
            keyExpr="_key"
            height="100%"
            t={lt}
            selectionEnabled={false}
            noDataText={lt('noData')}
            onRowClick={(data: ItemWarehouseParam) => onSelect(data)}
            onAddNew={() => { setItemCode(''); setLines([]); }}
            onRefresh={reloadList}
          />
        </SplitterItem>

        <SplitterItem size="55%" minSize="160px" collapsible>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderBottom: '1px solid #e5e7eb' }}>
              <span style={{ fontSize: 13, color: '#6b7280' }}>Malzeme Kodu</span>
              <div style={{ width: 220 }}>
                <TextBox value={itemCode} onValueChanged={(e) => setItemCode(e.value ?? '')} placeholder="Malzeme Kodu" />
              </div>
              <span style={{ flex: 1 }} />
              <Button text={lt('send')} icon="upload" type="default" stylingMode="contained" disabled={sending} onClick={send} />
            </div>
            <div style={{ flex: 1, minHeight: 0, padding: 12 }}>
              <DataGrid
                dataSource={lines}
                keyExpr="whNumber"
                height="100%"
                showBorders
                columnAutoWidth
                onRowInserted={() => setLines([...linesRef.current])}
                onRowRemoved={() => setLines([...linesRef.current])}
              >
                <Scrolling mode="virtual" />
                <Editing mode="cell" allowAdding allowUpdating allowDeleting useIcons />
                <Column dataField="whNumber" caption="Ambar No" dataType="number" width={100} />
                <Column dataField="whCode" caption="Ambar Adi" />
                <Column dataField="minLevel" caption="Min" dataType="number" width={90} />
                <Column dataField="maxLevel" caption="Max" dataType="number" width={90} />
                <Column dataField="safetyLevel" caption="Guvenlik" dataType="number" width={100} />
                <Column dataField="backorderFlag" caption="Negatif Stok" dataType="number" width={110} />
                <Column dataField="locationCode" caption="Stok Yeri" width={110} />
                <Column dataField="abcCode" caption="ABC" width={80} />
              </DataGrid>
            </div>
          </div>
        </SplitterItem>
      </Splitter>
    </div>
  );
}
